import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { Features } from '@ultrasnip/contracts';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { AuthLayout } from '@/components/shell/auth-layout';
import { Button } from '@/components/ui/button';
import { FormAlert } from '@/features/auth/form-parts';
import { authClient, authErrorMessage } from '@/lib/auth-client';

export const Route = createFileRoute('/_auth/check-email')({
  // Needs outgoing email, which is switched off (see Features.email).
  beforeLoad: () => {
    if (!Features.email) throw redirect({ to: '/login' });
  },
  validateSearch: z.object({ email: z.string().max(320).optional() }),
  component: CheckEmailScreen,
});

const RESEND_COOLDOWN_SECONDS = 60;

function CheckEmailScreen() {
  const { email } = Route.useSearch();
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const resend = async () => {
    if (!email) return;
    setError(null);
    const { error: sendError } = await authClient.sendVerificationEmail({ email, callbackURL: '/' });
    if (sendError) {
      setError(authErrorMessage(sendError));
      return;
    }
    setSent(true);
    setCooldown(RESEND_COOLDOWN_SECONDS);
  };

  return (
    <AuthLayout
      title="Check your email"
      footer={
        <Link to="/login" className="link">
          Back to sign in
        </Link>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-foreground">
          We sent a confirmation link to{' '}
          <span className="font-medium break-all">{email ?? 'your email address'}</span>.
        </p>
        <FormAlert message={error} />
        {sent && (
          <p className="text-[0.8125rem] text-muted-foreground" role="status">
            A new link is on its way.
          </p>
        )}
        {email && (
          <Button
            variant="secondary"
            onClick={() => void resend()}
            disabled={cooldown > 0}
            className="h-9 w-full"
          >
            {cooldown > 0 ? `Send another link in ${cooldown}s` : 'Send another link'}
          </Button>
        )}
      </div>
    </AuthLayout>
  );
}
